import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Edit2, Plus, Upload } from 'react-feather';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Loader,
  Page,
  PageHeader,
  SearchInput,
  Select,
  Table,
  TextInput,
  Toolbar,
} from '../../../ui';
import type { BadgeTone } from '../../../ui';
import { Avatar, Drawer, Swatch } from './ui-pending';
import './AgentAdminPage.scss';
import {
  agentAvailability,
  DEFAULT_WORKING_HOURS,
  fetchTasksMe,
  isTasksLeaderRole,
  RM_TASKS_ENABLED,
  tasksApi,
  TasksRole,
  WorkingHours,
} from '../../../Tasks/tasksApi';
import { uploadAgentPhoto } from '../../../Tasks/agentPhoto';

/** Full agent record from GET /v1/agents. */
interface Agent {
  id: string;
  externalId: string;
  name: string;
  email: string;
  role: TasksRole;
  isActive: boolean;
  workingDays: string[];
  workingHours?: WorkingHours;
  photoUrl?: string | null;
  /** The RM's own number, E.164, or null until someone records one. */
  phoneE164?: string | null;
  /**
   * The server's own verdict, which is the only one that knows about leave —
   * `agentAvailability` below reads the roster alone and would call someone
   * on a fortnight's leave "Available".
   */
  availability?: 'ON_SHIFT' | 'OFF_SHIFT' | 'ON_LEAVE';
  /** Last day of the leave they are on, inclusive. */
  onLeaveUntil?: string | null;
  team?: { key: string; name: string } | null;
}

interface Team {
  id: string;
  key: string;
  name: string;
}

const ROLES: TasksRole[] = ['AGENT', 'TEAM_LEADER', 'ADMIN'];
const DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

/**
 * The window to offer a working day that has none of its own.
 *
 * Deliberately not the global 10:00-19:00 default. An RM who works
 * 15:00-23:59 every other day does not start at 10 on the day a leader has
 * just added — and adding a day is exactly how a week off gets moved. Using
 * the global default here put two RMs on the 3PM-12AM desk onto a 10-7 shift
 * for two days a week, silently, and the system then routed tickets to them
 * five hours before they started work.
 *
 * So: the window this RM already works most often, ties broken by the
 * earliest day of the week so the answer never depends on key order. Falls
 * back to the global default only when there is nothing to go on.
 */
export function prevailingHours(hours: WorkingHours): { start: string; end: string } {
  let best: { hrs: { start: string; end: string }; n: number } | null = null;
  const seen: Record<string, number> = {};
  for (let i = 0; i < DAYS.length; i += 1) {
    const h = hours?.[DAYS[i]];
    if (!h || !h.start || !h.end) continue;
    const key = `${h.start}-${h.end}`;
    seen[key] = (seen[key] ?? 0) + 1;
    // Strictly greater keeps the earliest day on a tie: a later window has to
    // beat the running best outright to replace it.
    if (!best || seen[key] > best.n) best = { hrs: { start: h.start, end: h.end }, n: seen[key] };
  }
  return best ? best.hrs : DEFAULT_WORKING_HOURS;
}

const ROLE_TONE: Record<TasksRole, BadgeTone> = {
  AGENT: 'primary',
  TEAM_LEADER: 'info',
  ADMIN: 'danger',
};

interface EditState {
  externalId: string;
  name: string;
  email: string;
  teamKey: string;
  role: TasksRole;
  isActive: boolean;
  workingDays: string[];
  workingHours: WorkingHours;
  photoUrl: string;
  phoneE164: string;
  isNew: boolean;
}

/**
 * Numbers get pasted out of sheets — "88262 19281", "08655603963",
 * "+91 8976768329" — so punctuation is stripped and a missing country code is
 * taken as India, matching common/phone.ts on the server. An explicit `+`
 * always wins, so an RM abroad works if their number is written in full.
 */
const IN_NATIONAL = /^(?:0|91)?([6-9]\d{9})$/;

/** E.164, or null when it can't be read as a number. '' means "no number". */
const toE164 = (raw: string): string | null => {
  const cleaned = raw.replace(/[\s()\-.]/g, '');
  if (!cleaned) return '';
  if (cleaned.startsWith('+')) return /^\+[1-9]\d{7,14}$/.test(cleaned) ? cleaned : null;
  const india = IN_NATIONAL.exec(cleaned);
  return india ? `+91${india[1]}` : null;
};

const blankEdit = (teamKey: string): EditState => ({
  externalId: '',
  name: '',
  email: '',
  teamKey,
  role: 'AGENT',
  isActive: true,
  workingDays: ['MON', 'TUE', 'WED', 'THU', 'FRI'],
  workingHours: {},
  photoUrl: '',
  phoneE164: '',
  isNew: true,
});

const AgentAdminPage: React.FC = () => {
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [agents, setAgents] = useState<Agent[] | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [search, setSearch] = useState('');
  const [edit, setEdit] = useState<EditState | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  /** Upload a chosen image straight to S3 via a presigned URL, then set photoUrl. */
  const uploadPhoto = async (file: File): Promise<void> => {
    if (!edit) return;
    if (!edit.externalId.trim()) {
      toast.warn('Enter the External ID first');
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.warn('Pick an image file');
      return;
    }
    setUploading(true);
    try {
      const publicUrl = await uploadAgentPhoto(edit.externalId.trim(), file);
      setEdit((prev) => (prev ? { ...prev, photoUrl: publicUrl } : prev));
      toast.success('Photo uploaded');
    } catch (err: any) {
      toast.error(err.message || 'Photo upload failed');
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    fetchTasksMe().then((me) => setAllowed(isTasksLeaderRole(me?.role)));
  }, []);

  const load = useCallback(async () => {
    try {
      const [ags, tms] = await Promise.all([
        tasksApi<Agent[]>('/v1/agents'),
        tasksApi<Team[]>('/v1/teams'),
      ]);
      setAgents(ags);
      setTeams(tms);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load agents');
      setAgents([]);
    }
  }, []);

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = agents ?? [];
    if (!q) return list;
    return list.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        a.email.toLowerCase().includes(q) ||
        a.externalId.includes(q) ||
        (a.phoneE164 ?? '').includes(q),
    );
  }, [agents, search]);

  const openEdit = (a: Agent): void =>
    setEdit({
      externalId: a.externalId,
      name: a.name,
      email: a.email,
      teamKey: a.team?.key ?? teams[0]?.key ?? '',
      role: a.role,
      isActive: a.isActive,
      workingDays: a.workingDays ?? [],
      workingHours: a.workingHours ?? {},
      photoUrl: a.photoUrl ?? '',
      phoneE164: a.phoneE164 ?? '',
      isNew: false,
    });

  const save = async (): Promise<void> => {
    if (!edit) return;
    if (!edit.externalId.trim() || !edit.name.trim() || !edit.email.trim() || !edit.teamKey) {
      toast.warn('External ID, name, email and team are required');
      return;
    }
    // Catch it here rather than let the server 400 — the fix is always the
    // same and the admin shouldn't have to read a validation error to find it.
    const phone = toE164(edit.phoneE164);
    if (phone === null) {
      toast.warn('That phone number doesn’t look right — 10 digits for India, or the full number with its country code');
      return;
    }
    setSaving(true);
    try {
      await tasksApi('/v1/agents', {
        method: 'POST',
        body: JSON.stringify({
          externalId: edit.externalId.trim(),
          name: edit.name.trim(),
          email: edit.email.trim(),
          teamKey: edit.teamKey,
          role: edit.role,
          isActive: edit.isActive,
          workingDays: edit.workingDays,
          // Persist the *effective* hours for every working day — explicit
          // if the admin touched that day's time picker, else the same
          // default the picker was already showing. Filtering to only
          // explicitly-touched days silently saved {} whenever the admin
          // left the pre-filled defaults alone, which is why hours kept
          // coming back empty after a save.
          workingHours: Object.fromEntries(
            edit.workingDays.map((d) => [d, edit.workingHours[d] ?? prevailingHours(edit.workingHours)]),
          ),
          photoUrl: edit.photoUrl.trim() || undefined,
          // Always sent, unlike photoUrl: on this form an emptied box means
          // "remove the number", and the server reads "" as a clear.
          phoneE164: phone,
        }),
      });
      toast.success(`${edit.name} saved`);
      setEdit(null);
      await load();
    } catch (err: any) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const header = (actions?: React.ReactNode) => (
    <PageHeader
      eyebrow="RM Tasks"
      title="Relationship Managers"
      subtitle="Manage RM profiles, roles, teams and rosters."
      actions={actions}
    />
  );

  if (!RM_TASKS_ENABLED) {
    return (
      <Page>
        {header()}
        <EmptyState title="Not enabled" description="This feature isn't enabled in this environment yet." />
      </Page>
    );
  }
  if (allowed === null) {
    return (
      <Page>
        {header()}
        <Loader />
      </Page>
    );
  }
  if (!allowed) {
    return (
      <Page>
        {header()}
        <EmptyState title="Leaders only" description="You need a team-lead or admin role to manage agents." />
      </Page>
    );
  }

  const phoneE164 = edit ? toE164(edit.phoneE164) : '';

  return (
    <Page>
      {header(
        <Button variant="primary" icon={<Plus size={16} aria-hidden />} onClick={() => setEdit(blankEdit(teams[0]?.key ?? ''))}>
          Add agent
        </Button>,
      )}

      <Toolbar>
        <Field label="Search" className="ds-toolbar__grow aa-search">
          {(f) => (
            <SearchInput
              {...f}
              size="sm"
              placeholder="Search name, email, id or phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          )}
        </Field>
      </Toolbar>

      <Card flush>
        {agents === null ? (
          <Loader label="Loading agents…" />
        ) : filtered.length === 0 ? (
          <EmptyState
            title="No agents match."
            description={search.trim() ? 'Try a different name, email, id or phone.' : 'Add an agent to get started.'}
          />
        ) : (
          <Table caption="Relationship managers">
            <thead>
              <tr>
                <th>Agent</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Team</th>
                <th>Working days</th>
                <th>Availability</th>
                <th>Status</th>
                <th className="aa-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => {
                // Leave first, and from the server: this page used to read the
                // roster alone, so an RM two days into a fortnight off showed as
                // "Available" here while every other screen had them on leave.
                const onLeave = a.availability === 'ON_LEAVE';
                const av = agentAvailability(a.workingDays, a.workingHours);
                const available = !onLeave && av.available;
                const availText = onLeave ? 'On leave' : available ? 'Available' : 'Unavailable';
                const availLabel = onLeave
                  ? a.onLeaveUntil
                    ? `On leave until ${a.onLeaveUntil}`
                    : 'On leave'
                  : av.label;
                return (
                  <tr key={a.externalId}>
                    <td>
                      <div className="aa-agent">
                        <Avatar name={a.name} photoUrl={a.photoUrl} size="md" />
                        <div>
                          <div className="ds-strong">{a.name}</div>
                          <div className="rm-caption">{a.email}</div>
                          <div className="rm-caption">UserId · {a.externalId}</div>
                        </div>
                      </div>
                    </td>
                    {/* Missing is the state worth spotting, so it says so
                        rather than showing a dash like an empty column. */}
                    <td className="aa-nowrap ds-num">
                      {a.phoneE164 ? (
                        <a href={`tel:${a.phoneE164}`} onClick={(e) => e.stopPropagation()}>
                          {a.phoneE164}
                        </a>
                      ) : (
                        <Badge tone="warning">Not set</Badge>
                      )}
                    </td>
                    <td>
                      <Badge tone={ROLE_TONE[a.role] ?? 'neutral'}>{a.role.replace('_', ' ')}</Badge>
                    </td>
                    <td>{a.team?.name ?? <span className="ds-text-muted">—</span>}</td>
                    <td className="rm-caption">
                      {a.workingDays?.length ? a.workingDays.join(', ') : '—'}
                    </td>
                    <td>
                      <span
                        className={`aa-avail${available ? ' aa-avail--on' : ''}`}
                        style={onLeave ? { color: 'var(--ds-danger)' } : undefined}
                        title={availLabel}
                      >
                        <Swatch shape="dot" tone={onLeave ? 'danger' : available ? 'success' : 'neutral'} />
                        {availText}
                      </span>
                    </td>
                    <td>
                      {a.isActive ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Inactive</Badge>}
                    </td>
                    <td className="aa-actions-col">
                      <Button size="sm" icon={<Edit2 size={14} aria-hidden />} onClick={() => openEdit(a)}>
                        Edit
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>

      {/* Edit / add drawer */}
      <Drawer
        open={!!edit}
        onClose={() => setEdit(null)}
        title={edit?.isNew ? 'Add agent' : 'Edit agent'}
        size="md"
        footer={
          edit && (
            <Button variant="primary" block loading={saving} onClick={save}>
              {saving ? 'Saving…' : edit.isNew ? 'Create agent' : 'Save changes'}
            </Button>
          )
        }
      >
        {edit && (
          <>
            <div className="aa-photo">
              <Avatar name={edit.name || '?'} photoUrl={edit.photoUrl} size="lg" />
              <div className="ds-stack aa-photo__controls">
                <div className="rm-row">
                  <label
                    className={`ds-btn ds-btn--secondary ds-btn--sm aa-upload${uploading ? ' aa-upload--busy' : ''}`}
                  >
                    <Upload size={14} aria-hidden />
                    {uploading ? 'Uploading…' : 'Upload photo'}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      disabled={uploading}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadPhoto(f);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  {edit.photoUrl && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="aa-remove"
                      onClick={() => setEdit({ ...edit, photoUrl: '' })}
                    >
                      Remove
                    </Button>
                  )}
                </div>
                <span className="rm-caption">JPEG / PNG / WEBP, uploaded to S3.</span>
              </div>
            </div>

            <Field label="External ID (CMS userId)">
              {(f) => (
                <TextInput
                  {...f}
                  value={edit.externalId}
                  disabled={!edit.isNew}
                  onChange={(e) => setEdit({ ...edit, externalId: e.target.value })}
                />
              )}
            </Field>

            <Field label="Name">
              {(f) => (
                <TextInput {...f} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
              )}
            </Field>

            <Field label="Email">
              {(f) => (
                <TextInput
                  {...f}
                  type="email"
                  value={edit.email}
                  onChange={(e) => setEdit({ ...edit, email: e.target.value })}
                />
              )}
            </Field>

            {/* Echo the saved form back: +91 gets added for you, and seeing
                it appear is how you catch a number that isn't Indian. */}
            <Field
              label="Phone"
              error={
                phoneE164 === null ? (
                  <>
                    Doesn&rsquo;t look like a number — 10 digits for India, or the full number with
                    its country code (+14155550123).
                  </>
                ) : undefined
              }
              hint={
                phoneE164 && phoneE164 !== edit.phoneE164.replace(/[\s()\-.]/g, '') ? (
                  <>Saves as <strong>{phoneE164}</strong></>
                ) : (
                  <>
                    Goes to the renewal ticket so teachers can ring this RM. 10 digits is fine
                    &mdash; +91 is added. Leave blank if you don&rsquo;t have it.
                  </>
                )
              }
            >
              {(f) => (
                <TextInput
                  {...f}
                  type="tel"
                  value={edit.phoneE164}
                  placeholder="8976768329"
                  onChange={(e) => setEdit({ ...edit, phoneE164: e.target.value })}
                />
              )}
            </Field>

            <Field label="Team">
              {(f) => (
                <Select
                  {...f}
                  value={edit.teamKey}
                  options={teams.map((t) => ({ value: t.key, label: t.name }))}
                  onChange={(e) => setEdit({ ...edit, teamKey: e.target.value })}
                />
              )}
            </Field>

            <Field label="Role">
              {(f) => (
                <Select
                  {...f}
                  value={edit.role}
                  options={ROLES.map((r) => ({ value: r, label: r.replace('_', ' ') }))}
                  onChange={(e) => setEdit({ ...edit, role: e.target.value as TasksRole })}
                />
              )}
            </Field>

            <fieldset className="ds-field aa-fieldset">
              <legend className="ds-field__label">Working days</legend>
              <div className="aa-days">
                {DAYS.map((d) => {
                  const on = edit.workingDays.includes(d);
                  return (
                    <button
                      key={d}
                      type="button"
                      aria-pressed={on}
                      className={`aa-day${on ? ' aa-day--on' : ''}`}
                      onClick={() => setEdit({ ...edit, workingDays: on ? edit.workingDays.filter((x) => x !== d) : [...edit.workingDays, d] })}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <fieldset className="ds-field aa-fieldset">
              <legend className="ds-field__label">Working hours (IST)</legend>
              {edit.workingDays.length === 0 ? (
                <span className="rm-caption">Pick working days first.</span>
              ) : (
                DAYS.filter((d) => edit.workingDays.includes(d)).map((d) => {
                  const hrs = edit.workingHours[d] ?? prevailingHours(edit.workingHours);
                  const setHrs = (patch: Partial<{ start: string; end: string }>) =>
                    setEdit({
                      ...edit,
                      workingHours: { ...edit.workingHours, [d]: { ...hrs, ...patch } },
                    });
                  // HH:MM is zero-padded 24h, so a plain string compare tells
                  // us the window runs past midnight — e.g. 23:00 > 08:00.
                  const crossesMidnight = !!hrs.start && !!hrs.end && hrs.start > hrs.end;
                  return (
                    <div key={d} className="aa-hours">
                      <span className="aa-hours__day">{d}</span>
                      <TextInput
                        type="time"
                        size="sm"
                        className="aa-hours__time"
                        aria-label={`${d} start`}
                        value={hrs.start}
                        onChange={(e) => setHrs({ start: e.target.value })}
                      />
                      <span className="ds-text-muted">–</span>
                      <TextInput
                        type="time"
                        size="sm"
                        className="aa-hours__time"
                        aria-label={`${d} end`}
                        value={hrs.end}
                        onChange={(e) => setHrs({ end: e.target.value })}
                      />
                      {crossesMidnight && (
                        <span title="This shift runs past midnight — the end time is the next day">
                          <Badge tone="primary">+1 day</Badge>
                        </span>
                      )}
                    </div>
                  );
                })
              )}
              <span className="ds-field__hint">
                A day left unset takes this RM's usual window
                ({prevailingHours(edit.workingHours).start}–{prevailingHours(edit.workingHours).end}), not a
                fixed default — so adding a day never quietly moves their shift.
              </span>
            </fieldset>

            <label className="aa-check">
              <input type="checkbox" checked={edit.isActive} onChange={(e) => setEdit({ ...edit, isActive: e.target.checked })} />
              <span>Active</span>
            </label>
          </>
        )}
      </Drawer>
    </Page>
  );
};

export default AgentAdminPage;
